"use client"

import { useState } from "react"
import { CircleUserIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { UserData } from "@/lib/intershop/interfaces/user/user"
import { cn } from "@/lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/components"
import { UbHeaderStoryblok } from "@/components/storyblok/component-types"
import UbLoginForm from "@/components/storyblok/UbLoginForm"

import StoryblokAccountForm from "./StoryblokAccountForm"

interface StoryblokAccountPopupClientProps {
  blok: UbHeaderStoryblok
  userData?: UserData
  sbLocale: string
  disablePopup?: boolean
  variant?: "default" | "rebrush"
}

export default function StoryblokAccountPopupClient({
  blok,
  userData,
  sbLocale,
  disablePopup = false,
  variant = "default",
}: StoryblokAccountPopupClientProps) {
  const [popUpOpen, setPopUpOpen] = useState(false)
  const t = useTranslations("storefront")
  const triggerClassName = cn(
    "flex flex-row items-center justify-start",
    variant === "rebrush"
      ? `
        h-10 gap-1 rounded-[4px] px-1.5 text-sm leading-4 font-semibold text-[#303030]
        hover:bg-[#F8F8F8] focus-visible:ring-2 focus-visible:ring-ub-hlblue focus-visible:outline-hidden
        xs:min-w-10 lg:min-w-0
      `
      : "gap-2 font-semibold text-gray-600 xs:min-w-5 lg:min-w-36"
  )
  const iconClassName = variant === "rebrush" ? "size-7 text-[#303030]" : "h-[20px] text-black"

  if (userData && disablePopup) {
    return (
      <div className="flex h-full rounded-[1.25rem]">
        <button type="button" aria-disabled="true" className={triggerClassName}>
          <span className="relative">
            <CircleUserIcon className={iconClassName} strokeWidth={variant === "rebrush" ? 1.6 : 2} />
            {variant === "rebrush" && (
              <span className="absolute right-0 bottom-0 size-2.5 rounded-full bg-primary ring-2 ring-white lg:hidden" />
            )}
          </span>
          <p
            className={cn(
              "hidden text-left tracking-tight lg:block",
              variant === "rebrush" ? "text-xs leading-[14px] font-semibold" : "leading-tight"
            )}
          >
            {variant === "rebrush" ? (
              <>
                <span className="block">{userData.firstName}</span>
                <span className="block">{userData.lastName}</span>
              </>
            ) : (
              userData.firstName + " " + userData.lastName
            )}
          </p>
        </button>
      </div>
    )
  }

  return (
    <div className="flex h-full rounded-[1.25rem]">
      <>
        {userData ? (
          <Popover open={popUpOpen} onOpenChange={setPopUpOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                onClick={() => setPopUpOpen((o) => !o)}
                aria-expanded={popUpOpen}
                className={triggerClassName}
              >
                <span className="relative">
                  <CircleUserIcon className={iconClassName} strokeWidth={variant === "rebrush" ? 1.6 : 2} />
                  {variant === "rebrush" && (
                    <span className="absolute right-0 bottom-0 size-2.5 rounded-full bg-primary ring-2 ring-white lg:hidden" />
                  )}
                </span>
                <p
                  className={cn(
                    "hidden text-left tracking-tight lg:block",
                    variant === "rebrush" ? "text-xs leading-[14px] font-semibold" : "leading-tight"
                  )}
                >
                  {variant === "rebrush" ? (
                    <>
                      <span className="block">{userData.firstName}</span>
                      <span className="block">{userData.lastName}</span>
                    </>
                  ) : (
                    userData.firstName + " " + userData.lastName
                  )}
                </p>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto rounded-none border-black p-6" align={"end"} alignOffset={-80}>
              <div className="w-[260px]">
                <StoryblokAccountForm blok={blok} userData={userData} sbLocale={sbLocale} />
              </div>
            </PopoverContent>
          </Popover>
        ) : (
          <Popover open={popUpOpen} onOpenChange={setPopUpOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                onClick={() => setPopUpOpen((o) => !o)}
                aria-label={t("account.nav.menu")}
                aria-expanded={popUpOpen}
                className={triggerClassName}
              >
                <CircleUserIcon className={iconClassName} strokeWidth={variant === "rebrush" ? 1.6 : 2} />
                <p className="hidden whitespace-nowrap lg:block">{t("account.nav.menu")}</p>
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto overflow-visible rounded-[1.25rem] p-6 lg:mr-20"
              align={"end"}
              alignOffset={-80}
            >
              <div className="w-[260px] lg:w-[570px]">
                <UbLoginForm blok={blok} sbLocale={sbLocale} />
              </div>
            </PopoverContent>
          </Popover>
        )}
      </>
    </div>
  )
}
